import React, { useRef, useState } from 'react';
import { Image, PixelRatio, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import Icon from 'react-native-vector-icons/FontAwesome';
import { useHover } from 'react-native-web-hooks';
import { StateWithHistory } from 'redux-undo';
import Game from '../../shell/Game';
import useFontSize from '../../shell/hooks/useFontSize';
import { onDeleteSave, onSaveFile } from '../../shell/utils/appManagementFunctions';
import { getMetaDataFromSave } from '../../shell/utils/getMetaDataFromSave';
import { useAppSelector } from '../../store/hooks';
import { SeenWords } from '../../store/slices/AdvanceSlice';
import { GameObject } from '../../store/slices/gameObjectSlice';
import { TInstaINT } from '../../store/slices/instaSlice';
import { selectMenu, TSettings } from '../../store/slices/menuSlice';
import { store } from '../../store/store';
import { PromptQuestionModal } from '../PromptQuestionModal';
import { TScenesReplay } from './Scenes/replayScenes/ScenesReplay';
import { MenuOptions } from './SideBar';
import { TrashButton } from './TrashButton';
import { TsoundsINT } from '../../store/slices/soundsSlice';




export interface PersistentFile {
    settings: TSettings,
    gallery: { [key: string]: TScenesReplay; };
    globalSeenScenarios: string[];
    globalSeenImages: string[];
    seenWords: SeenWords,
}
export interface AndroidSaves {
    [key: string]: saveObject;
}


interface SaveLoadProps {
    index?: number,
    file: saveObjectMetaData,
    onSavePressed?: (save?: saveObjectMetaData) => void,
    onLoadPressed?: (index: number) => void,

}
export interface saveObjectMetaData {
    image?: string,
    customName: string | undefined,
    timestamp: Date,
    userComment?: string,
    folder: string,
    page: number | string,
    slot: number,
}
export interface saveObject extends saveObjectMetaData {
    gameObject: StateWithHistory<GameObject>,
    insta: StateWithHistory<TInstaINT>,
    // phone: StateWithHistory<TPhoneINT>,
    sounds: TsoundsINT,
    persistent: PersistentFile,
}
export const _doSave = async (file: saveObjectMetaData, index: number, saveCustomName: string, overwrite?: boolean) => {
    const gameObject = store.getState().gameObject;
    const insta = store.getState().insta;
    // const phone = store.getState().phone;
    const menu = store.getState().menu;
    const seenWords = store.getState().advance.seenWords as SeenWords;
    const globalSeenScenarios = [...new Set([...(store.getState().advance.globalSeenScenarios || []), ...gameObject.present.seenScenarios])];
    const globalSeenImages = store.getState().advance.globalSeenImages;
    const saveObject: saveObject = {
        page: file?.page ?? menu.settings.page!,
        persistent: { settings: menu.settings, seenWords, globalSeenScenarios, globalSeenImages: (globalSeenImages || []), gallery: menu.gallery },
        slot: index!,
        sounds: store.getState().sounds,
        gameObject: { ...gameObject, present: { ...gameObject.present, version: Game.version } },
        folder: menu.settings.folder,
        insta,
        customName: saveCustomName || file?.customName,
        timestamp: new Date(),
    };
    return onSaveFile(overwrite ? getMetaDataFromSave(saveObject) : { ...file, ...saveObject });

};


export const SaveLoad: React.FC<SaveLoadProps> = React.memo(({ file, onSavePressed, onLoadPressed, index }) => {
    const ref = useRef(null);
    const isHovered = useHover(ref);
    const [loadApprove, setLoadApprove] = useState(false);
    const textPressed = useRef(false);
    const defaultText = useRef<string>();
    const pencilSize = useRef(useFontSize(1.5 / PixelRatio.get()));
    const timesSize = useRef(useFontSize(1.7 / PixelRatio.get()));

    const saveCustomName = useRef<string | undefined>();
    // const [save, setSave] = useState<string>();
    const menuSlice = useAppSelector(selectMenu);

    const styles = StyleSheet.create({
        container: {
            height: "100%",
            marginBottom: 30,
        },
        containerHover: {
            opacity: 1
        },
        imageContainer: {
            flex: 4
        },
        images: {
            width: "100%",
            height: "100%",
            aspectRatio: 16 / 9
        },
        emptyImage: {
            width: "100%",
            height: "100%",
            backgroundColor: "gray"
        },
        textContainer: {
            height: useFontSize(2),
        },
        buttonText: {
            alignSelf: "center",
            alignItems: "center",
            color: "white",
            fontSize: useFontSize(1.5),
            alignContent: "center",



        }, saveSlot: {
            aspectRatio: 16 / 9,
        },
        pencilIcon: {
            position: "absolute",
            color: "white",
            padding: 10 / PixelRatio.get(),
        },
        timesIcon: {
            position: "absolute",
            color: "white",
            padding: 10 / PixelRatio.get(),
            right: 0

        }

    });

    const _onLoadPressed = () => {
        if (onLoadPressed) {
            onLoadPressed(index!);
        }
    };

    const onPressed = () => {
        if (menuSlice.menuState === MenuOptions.SAVE) {
            if (file?.page === "A") { return; } //autosave
            defaultText.current = new Date().toLocaleString();
            if (file) {

                setLoadApprove(true);
                return;
            }
            return _onSavePressed();
        }

        _onLoadPressed();

    };
    const onTextPressed = () => {
        if (!file) { return; }
        defaultText.current = new Date().toLocaleString();
        textPressed.current = true;
        setLoadApprove(true);

    };
    const onTrashPressed = (approved: string | boolean) => {
        if (!file) { return; }
        if (!approved) { return; }

        onDeleteSave({ ...file, folder: menuSlice.settings.folder }).then(res => {
            if (res) {
                onSavePressed?.();
            }
        });

    };
    const _onSavePressed = async (overwrite?: boolean) => {
        _doSave(file, index!, saveCustomName.current!, overwrite)
            .then(res => {
                if (onSavePressed && res) {
                    onSavePressed(getMetaDataFromSave(res));
                }
                textPressed.current = false;
                saveCustomName.current = '';
            });

    };
    const onSubmit = (input: string | boolean) => {
        setLoadApprove(false);
        if (!input) { return; }

        if (typeof input === "string" && input !== defaultText.current) {
            saveCustomName.current = input;
        }

        if (textPressed.current && file) {
            return _onSavePressed(true);
        }


        _onSavePressed();

    };
    return (
        <View style={{
            width: "25%",
            marginTop: 5,
            transform: [{ scale: isHovered ? 1.1 : 1 }],
            zIndex: isHovered ? 5 : 1,
        }}>
            <View style={styles.saveSlot}>
                <View style={[{ borderWidth: 4, borderColor: isHovered ? "whitesmoke" : "#b4a4a4", margin: 2, height: "100%" }]}>
                    {loadApprove ?
                        <PromptQuestionModal showCancel={true} defaultValue={file?.customName || defaultText.current} title={textPressed.current ? "Rename save:" : "Overwrite existing save?"} placeHolder={"Enter save name..."} onSubmit={(input) => onSubmit(input)}></PromptQuestionModal>
                        : <></>}
                    <TouchableOpacity onPress={() => onPressed()} ref={ref} style={[styles.container, isHovered && styles.containerHover,]}>
                        <View style={styles.imageContainer}>
                            {file?.image ? <Image style={styles.images} source={{ uri: file.image }} resizeMode={"contain"}></Image> : <View style={styles.emptyImage}></View>}
                        </View>

                    </TouchableOpacity>
                    {file ? <Icon name="pencil" size={pencilSize.current} style={styles.pencilIcon} onPress={() => onTextPressed()} /> : <></>}
                    {/* {file ? <Icon name="trash" size={timesSize.current} style={styles.timesIcon} onPress={() => onTrashPressed()} /> : <></>} */}
                    {file ? <TrashButton modalProps={{ showCancel: true, hideInputField: true, title: "Are you sure you want to delete this save?" }}
                        onClick={(approved) => onTrashPressed(approved)}>

                    </TrashButton> : <></>}

                </View>
            </View>
            <View style={styles.textContainer}>
                {/* TODO: date format */}
                <Text selectable={false} adjustsFontSizeToFit={true} numberOfLines={1} style={styles.buttonText}> {file?.timestamp || file?.customName ? file?.customName || new Date(file.timestamp).toLocaleString() : ''}</Text>
            </View>

        </View>
    );
});